import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Slide,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Paper,
  Button,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import MessageIcon from "@mui/icons-material/Message";
import CallIcon from "@mui/icons-material/Call";
import StorageIcon from "@mui/icons-material/Storage";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";
import SecurityIcon from "@mui/icons-material/Security";
import BlockIcon from "@mui/icons-material/Block";
import LanguageIcon from "@mui/icons-material/Language";
import ApiIcon from "@mui/icons-material/Api";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WidgetsIcon from "@mui/icons-material/Widgets";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ProductCard from "./ProductCard";

// Define product data
const productCategories = [
  { id: "all", label: "All Products", icon: <WidgetsIcon fontSize="small" /> },
  {
    id: "messaging",
    label: "Messaging",
    icon: <MessageIcon fontSize="small" />,
  },
  { id: "voice", label: "Voice", icon: <CallIcon fontSize="small" /> },
  {
    id: "security",
    label: "Security",
    icon: <SecurityIcon fontSize="small" />,
  },
  { id: "api", label: "API & Analytics", icon: <ApiIcon fontSize="small" /> },
];

const products = [
  {
    id: 1,
    title: "Bulk Messaging Gateway (BMG)",
    description:
      "A telecom-grade SMS platform engineered for high-volume throughput and compliance.",
    icon: <MessageIcon />,
    iconBgColor: "#2196f3",
    category: "messaging",
    features: [
      "1M+ SMS per minute delivery capacity",
      "SMPP v3.4/5.0 and HTTP APIs",
      "Multi-operator routing with cost optimization",
      "Real-time DLR (Delivery Receipt) engine",
      "DLT integration for India regulatory compliance",
    ],
    tags: ["SMS", "High Volume", "API", "Compliance"],
    highlight: true,
  },
  {
    id: 2,
    title: "Voice Communications Platform",
    description:
      "Cloud-hosted voice platform offering comprehensive calling features and IVR capabilities.",
    icon: <CallIcon />,
    iconBgColor: "#e91e63",
    category: "voice",
    features: [
      "Dynamic outbound/inbound calling APIs",
      "Voice IVR builder with drag-drop workflows",
      "Call bridging, tracking, and masking",
      "TTS and ASR integration",
      "Multi-lingual and regional language support",
    ],
    tags: ["Voice", "IVR", "Cloud", "Calling"],
    highlight: true,
  },
  {
    id: 3,
    title: "App-Layer SMSC",
    description:
      "Custom-built Application Short Message Service Center with direct-to-operator connectivity.",
    icon: <StorageIcon />,
    iconBgColor: "#ff9800",
    category: "messaging",
    features: [
      "Direct-to-operator connectivity",
      "High availability cluster mode",
      "Supports 100K+ TPS throughput",
      "Encrypted message flow and secure APIs",
      "In-built queuing, retry, and throttling",
    ],
    tags: ["SMSC", "High Throughput", "Security", "Scalable"],
    highlight: true,
  },
  {
    id: 4,
    title: "Call Completion Suite",
    description:
      "Maximizes telecom and enterprise outreach by detecting unreachable users and providing alternatives.",
    icon: <PhoneCallbackIcon />,
    iconBgColor: "#4caf50",
    category: "voice",
    features: [
      "Detects unreachable or offline mobile users",
      "Automated voice alerts or missed call SMS",
      "Boosts customer engagement rates",
      "Enables revenue recovery for operators",
      "Customizable retry logic and voice fallback rules",
    ],
    tags: ["Voice Alerts", "Engagement", "Reachability"],
    highlight: false,
  },
  {
    id: 5,
    title: "SMS Firewall",
    description:
      "Advanced telecom-grade firewall for messaging control and protection.",
    icon: <SecurityIcon />,
    iconBgColor: "#673ab7",
    category: "security",
    features: [
      "Blocks grey routes, SIM box activity, and bypass traffic",
      "Integrates with MNOs, SS7/SMPP infrastructure",
      "DPI and signature-based threat detection",
      "Real-time analytics on A2P traffic patterns",
      "Fraud prevention and regulatory compliance",
    ],
    tags: ["Security", "Fraud Prevention", "Analytics"],
    highlight: false,
  },
  {
    id: 6,
    title: "SPAM Guard & UCC Detection",
    description:
      "AI-driven engine to detect and filter Unsolicited Commercial Communication.",
    icon: <BlockIcon />,
    iconBgColor: "#f44336",
    category: "security",
    features: [
      "Natural language processing for content analysis",
      "Traffic pattern analysis",
      "Real-time detection of promotional content",
      "Integrated with DLT platforms",
      "Dashboard for regulator reporting",
    ],
    tags: ["AI", "Spam Detection", "Compliance", "UCC"],
    highlight: false,
  },
  {
    id: 7,
    title: "Enterprise Messaging Aggregation",
    description:
      "Global A2P and P2A SMS termination with local and international routes.",
    icon: <LanguageIcon />,
    iconBgColor: "#009688",
    category: "messaging",
    features: [
      "Global A2P and P2A SMS termination",
      "Local and international routes",
      "Interconnect agreements with Tier-1 operators",
      "Enterprise billing and reconciliation",
      "Campaign performance tools",
    ],
    tags: ["Global", "Aggregation", "Enterprise"],
    highlight: false,
  },
  {
    id: 8,
    title: "Telecom API Gateway",
    description:
      "Unified API platform for SMS, voice, email, and RCS integration.",
    icon: <ApiIcon />,
    iconBgColor: "#3f51b5",
    category: "api",
    features: [
      "Unified API for multiple communication channels",
      "Supports JSON/XML/REST/SMPP integrations",
      "Custom adapters for operator backends",
      "Enterprise CRM integration",
      "Developer-friendly documentation",
    ],
    tags: ["API", "Integration", "Multi-channel"],
    highlight: true,
  },
  {
    id: 9,
    title: "DLR, CDR & Real-Time Analytics",
    description:
      "Accurate reporting and analytics for message delivery and call details.",
    icon: <AssessmentIcon />,
    iconBgColor: "#795548",
    category: "api",
    features: [
      "Real-time Delivery Reports and Call Detail Records",
      "Export to MySQL, Redis, Kafka, or ELK stack",
      "CDR reconciliation engine for billing",
      "Integration with Power BI and Grafana",
      "Custom reporting capabilities",
    ],
    tags: ["Analytics", "Reporting", "Billing"],
    highlight: false,
  },
  {
    id: 10,
    title: "WhatsApp Business Messaging Platform (WBMP)",
    description:
      "A carrier-grade WhatsApp communication platform built for scale, automation, and regulatory compliance.",
    icon: <MessageIcon />,
    iconBgColor: "#25D366",
    category: "messaging",
    features: [
      "High-throughput message delivery for transactional and conversational use cases",
      "Official WhatsApp Business API integration",
      "Advanced bot integration for automated customer interactions (chatbots, voice-to-chat, AI bots)",
      "Interactive message support (buttons, lists, quick replies)",
      "Template management with approval workflow",
      "Real-time message status, read receipts, and webhook callbacks",
      "Opt-in management and WhatsApp policy-compliant messaging",
      "Multi-tenant architecture with role-based access",
      "Seamless integration via REST APIs and webhooks",
    ],
    tags: ["WhatsApp", "Business API", "Automation", "Compliance"],
    highlight: true,
  },
  {
    id: 11,
    title: "AI Voice Bot Platform",
    description:
      "An enterprise-grade voice automation platform designed for high concurrency, low latency, and natural human interactions.",
    icon: <CallIcon />,
    iconBgColor: "#9c27b0",
    category: "voice",
    features: [
      "High-concurrency inbound and outbound call handling",
      "SIP, PRI, and PSTN integration with telecom carriers",
      "AI-powered voice bots with ASR, NLU, and TTS support",
      "Real-time call control with barge-in and DTMF handling",
      "Seamless handoff to live agents and CRM systems",
      "Call recording, transcripts, and analytics",
      "Multi-language and regional accent support",
      "Secure, scalable architecture with compliance-ready logging",
    ],
    tags: ["AI", "Voice Bot", "Automation", "Enterprise"],
    highlight: true,
  },
];

const GradientBox = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-50%",
    width: "200%",
    height: "100%",
    background: `linear-gradient(60deg, 
      ${theme.palette.primary.dark} 0%, 
      ${theme.palette.primary.main} 30%, 
      ${theme.palette.secondary.main} 70%, 
      ${theme.palette.secondary.dark} 100%)`,
    animation: "gradientAnimation 8s ease infinite",
  },
  "@keyframes gradientAnimation": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
}));

const ProductList = () => {
  const [currentCategory, setCurrentCategory] = useState("all");
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleCategoryChange = (event, newValue) => {
    setCurrentCategory(newValue);
  };

  const handleContactRedirect = () => {
    navigate("/contact");
  };

  const filteredProducts =
    currentCategory === "all"
      ? products
      : products.filter((product) => product.category === currentCategory);

  // Get highlighted products if we're showing all products
  const highlightedProducts = products.filter((product) => product.highlight);

  return (
    <>
      {/* Hero Section */}
      <GradientBox
        sx={{
          position: "relative",
          py: { xs: 8, md: 12 },
          color: "white",
          overflow: "hidden",
        }}
      >
        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Slide direction="down" in={true} timeout={800}>
            <Typography variant="h2" component="h1" fontWeight="bold">
              Our Products & Solutions
            </Typography>
          </Slide>
          <Slide direction="up" in={true} timeout={1000}>
            <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
              Empowering telecom communication with intelligent, secure, and
              scalable technologies
            </Typography>
          </Slide>
          <Slide direction="up" in={true} timeout={1500}>
            <Typography variant="body1" sx={{ mb: 3, maxWidth: 600 }}>
              From high-volume SMS handling to voice communications and advanced
              security systems, our comprehensive suite of products addresses
              the core challenges of modern telecommunications.
            </Typography>
          </Slide>
          <Fade in={true} timeout={1800}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                },
              }}
              onClick={handleContactRedirect}
            >
              Request Demo
            </Button>
          </Fade>
        </Container>
        {isMedium && (
          <>
            <Box
              sx={{
                position: "absolute",
                bottom: -50,
                right: -50,
                width: 300,
                height: 300,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: -30,
                left: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.05)",
                zIndex: 1,
              }}
            />
          </>
        )}
      </GradientBox>

      {/* Category Tabs - Fixed positioning and spacing */}
      <Box sx={{ py: 5, bgcolor: "#f9f9f9" }}>
        <Container>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 8,
              overflow: "hidden",
              maxWidth: "100%",
              mx: "auto",
              mb: 4,
            }}
          >
            <Tabs
              value={currentCategory}
              onChange={handleCategoryChange}
              variant={isMedium ? "scrollable" : "fullWidth"}
              scrollButtons={isMedium ? "auto" : false}
              allowScrollButtonsMobile
              centered={!isMedium}
              sx={{
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "2px 2px 0 0",
                },
              }}
            >
              {productCategories.map((category) => (
                <Tab
                  key={category.id}
                  icon={category.icon}
                  iconPosition="start"
                  label={category.label}
                  value={category.id}
                  sx={{
                    fontWeight: 600,
                    py: 2,
                    minHeight: 64,
                    textTransform: "none",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    "& .MuiTab-iconWrapper": {
                      marginRight: 1,
                      marginBottom: "0 !important",
                    },
                    "&.Mui-selected": {
                      color: "primary.main",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Paper>
        </Container>
      </Box>

      <Container sx={{ mb: 8 }}>
        {/* Featured Solutions Section */}
        {currentCategory === "all" && (
          <Box sx={{ mb: 8 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 4,
                borderBottom: "1px solid #eaeaea",
                pb: 2,
              }}
            >
              <FilterAltIcon color="primary" sx={{ mr: 2 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                Featured Solutions
              </Typography>
            </Box>
            <Grid container spacing={4}>
              {highlightedProducts.map((product) => (
                <Grid item xs={12} md={6} lg={3} key={product.id}>
                  <Fade in={true} timeout={500}>
                    <Box>
                      <ProductCard product={product} />
                    </Box>
                  </Fade>
                </Grid>
              ))}
            </Grid>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 8,
                mb: 4,
                borderBottom: "1px solid #eaeaea",
                pb: 2,
              }}
            >
              <WidgetsIcon color="primary" sx={{ mr: 2 }} />
              <Typography variant="h5" component="h2" fontWeight="bold">
                All Products
              </Typography>
            </Box>
          </Box>
        )}

        {/* Products Grid */}
        <Grid container spacing={4}>
          {filteredProducts.map((product, index) => (
            <Grid item xs={12} md={6} lg={4} key={product.id}>
              <Fade in={true} timeout={300 + index * 100}>
                <Box>
                  <ProductCard product={product} />
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
};

export default ProductList;
